import { datadogLogs, type StatusType } from "@datadog/browser-logs";
import { trace, type SpanContext } from "@opentelemetry/api";
import pino, { type Level, type Logger as PinoLoggerType } from "pino";
import { browser } from "$app/env";

import { PUBLIC_DD_ENV, PUBLIC_DD_SERVICE, PUBLIC_DD_VERSION, PUBLIC_LOG_LEVEL } from "$app/env/public";

function convertTraceID(id: string): string {
	if (id.length < 16) {
		return "";
	}
	if (id.length > 16) {
		id = id.substring(0, 16);
	}
	const intValue: number = Number.parseInt(id, 16);
	return intValue.toString(10);
}

const baseDatadogFields = {
	"dd.env": PUBLIC_DD_ENV,
	"dd.service": PUBLIC_DD_SERVICE,
	"dd.version": PUBLIC_DD_VERSION,
};

function convertLevel(level: number): StatusType {
	if (level >= 50) {
		return "error";
	}
	if (level >= 40) {
		return "warn";
	}
	if (level >= 30) {
		return "info";
	}
	return "debug";
}

interface PinoLogObject {
	time: number;
	level: number;
	msg: string;
	err?: unknown;
	[key: string]: unknown;
}

type LogFields = Record<string, unknown> & { error?: never };

class PinoLogger {
	private logger: PinoLoggerType;

	constructor() {
		this.logger = browser ? this.createClientLogger() : this.createServerLogger();
	}

	createServerLogger(): PinoLoggerType {
		return pino(
			pino.transport({
				targets: [
					{
						level: PUBLIC_LOG_LEVEL ?? "debug",
						target: "pino/file",
						options: { destination: 1 }, // this writes to STDOUT
					},
				],
			}),
		);
	}

	createClientLogger(): PinoLoggerType {
		return pino({
			level: PUBLIC_LOG_LEVEL ?? "debug",
			browser: {
				asObject: true,
				write: o => {
					const { time: _time, level, msg, err, ...rest } = o as PinoLogObject;
					const thrown = err instanceof Error ? err : undefined;
					if (err !== undefined && !thrown) {
						rest.err = err;
					}
					const fields = this.setDatadogFields(rest);
					datadogLogs.logger.log(msg, fields, convertLevel(level), thrown);
				},
			},
		});
	}

	setDatadogFields(fields: LogFields = {}): LogFields {
		const span = trace.getActiveSpan();
		if (span) {
			const spanContext: SpanContext = span.spanContext();
			return {
				...fields,
				...baseDatadogFields,
				"dd.trace_id": convertTraceID(spanContext.traceId),
				"dd.span_id": convertTraceID(spanContext.spanId),
			};
		} else {
			return { ...fields, ...baseDatadogFields };
		}
	}

	log(level: Level, message: string, fields: LogFields = {}): void {
		const enrichedFields = this.setDatadogFields(fields);
		switch (level) {
			case "trace":
				this.logger.trace(enrichedFields, message);
				break;
			case "debug":
				this.logger.debug(enrichedFields, message);
				break;
			case "info":
				this.logger.info(enrichedFields, message);
				break;
			case "warn":
				this.logger.warn(enrichedFields, message);
				break;
			case "error":
				this.logger.error(enrichedFields, message);
				break;
			case "fatal":
				this.logger.fatal(enrichedFields, message);
				break;
		}
	}

	trace(message: string, fields: LogFields = {}): void {
		this.log("trace", message, fields);
	}

	debug(message: string, fields: LogFields = {}): void {
		this.log("debug", message, fields);
	}

	info(message: string, fields: LogFields = {}): void {
		this.log("info", message, fields);
	}

	warn(message: string, fields: LogFields = {}): void {
		this.log("warn", message, fields);
	}

	error(message: string, fields: LogFields = {}): void {
		this.log("error", message, fields);
	}

	fatal(message: string, fields: LogFields = {}): void {
		this.log("fatal", message, fields);
	}
}

export const Logger = new PinoLogger();
